import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { alpha } from '@mui/system';
import { Tab, Card, Tabs, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { ListEntityType } from 'src/services/lists/lists.types';

import ListsTableView from 'src/sections/lists/lists-table-view';
import CompaniesList from 'src/sections/companies/table/companies/companies-list';

import { TenantType } from 'src/types/enums';

enum TabValue {
  COMPANIES = 'companies',
  LISTS = 'lists',
}

export default function CompaniesView() {
  const router = useRouter();

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedTab, setSelectedTab] = useState<TabValue | undefined>();

  const tenantType = useOrgTenant();

  useEffect(() => {
    if (!tenantType) return;

    if (tenantType === TenantType.Candidate) {
      router.push(paths.dashboard.root);
    }
  }, [router, tenantType]);


  // Set default tab
  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (selectedTab) return;

    let defaultTab: TabValue = TabValue.COMPANIES

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(TabValue).includes(existingTab as TabValue)) {
      defaultTab = existingTab as TabValue;
    }

    setSelectedTab(defaultTab);

  }, [selectedTab, searchParams]);

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!selectedTab) return;

    // Merge ?tab into the existing query, and only when out of sync, so it can't clobber the record
    // panel's co-owned ?preview params (JT1-1529) — including on a tab-less deep-link.
    if (searchParams.get('tab') === selectedTab) return;

    const next = new URLSearchParams(searchParams);
    next.set('tab', selectedTab);
    setSearchParams(next, { replace: true });
  }, [selectedTab, searchParams, setSearchParams]);

  return (
    <Card>
      <Tabs
        value={selectedTab}
        onChange={(_event, tab) => setSelectedTab(tab)}
        sx={{
          px: 2.5,
          boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
        }}
      >
        {Object.values(TabValue).map((tab) => (
          <Tab
            key={tab}
            iconPosition="end"
            value={tab}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {tab}
              </Typography>
            }
          />
        ))}
      </Tabs>

      {
        selectedTab === TabValue.COMPANIES &&
        <CompaniesList />
      }

      {
        selectedTab === TabValue.LISTS &&
        <ListsTableView
          entityType={ListEntityType.COMPANY}
        />
      }


    </Card>
  );
}
